<script setup>
import CookieConsentBanner from "@/Components/CookieConsentBanner.vue";
import { Head, Link, usePage } from "@inertiajs/vue3";
import { IconMenu2 } from "@tabler/icons-vue";
import { computed } from "vue";

const props = defineProps({
    fullWidth: {
        type: Boolean,
        default: false,
    },
    signupRouteName: {
        type: String,
        default: "signup.details.new",
    },
    signupButtonText: {
        type: String,
        default: "Sign Up Free",
    },
});

const page = usePage();
const user = page.props.auth.user;
const isLoggedIn = !!user;
const signupHref = computed(() => route(props.signupRouteName));
</script>
<template>
    <!-- Header Section -->
    <Head>
        <link
            head-key="front-stylesheet"
            rel="stylesheet"
            href="/template/allmyonline/assets/css/style.css?q=4"
        />
    </Head>
    <header class="header w-full max-w-6xl mx-auto">
        <nav class="navbar">
            <a href="/" class="text-decoration-none">
                <Logo />
            </a>
            <ul class="nav-links">
                <li><a :href="route('price.index')">Pricing</a></li>
                <li><a :href="route('help.index')">Help</a></li>
            </ul>
            <div class="nav-buttons" v-if="isLoggedIn">
                <Link
                    :href="route('dashboard')"
                    class="amo-btn amo-btn--primary"
                >
                    Dashboard</Link
                >
            </div>
            <div class="nav-buttons d-flex gap-3" v-else>
                <Link :href="route('login')" class="amo-btn amo-btn--secondary"
                    >Login</Link
                >
                <Link
                    :href="signupHref"
                    data-testid="signup-free-button"
                    class="amo-btn amo-btn--primary"
                    >{{ props.signupButtonText }}</Link
                >
            </div>
        </nav>

        <div class="mobile-nav">
            <Logo />

            <nav>
                <button
                    class="btn"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#mobileMenu"
                    aria-controls="mobileMenu"
                    style="color: var(--primary)"
                >
                    <IconMenu2 :size="24" />
                </button>

                <div
                    class="offcanvas offcanvas-end mobile-menu"
                    tabindex="-1"
                    style="z-index: 9999"
                    id="mobileMenu"
                    aria-labelledby="mobileMenuLabel"
                >
                    <div class="offcanvas-header mobile-menu__header">
                        <Logo />
                        <button
                            type="button"
                            class="btn-close text-reset"
                            data-bs-dismiss="offcanvas"
                            aria-label="Close"
                        ></button>
                    </div>
                    <div class="offcanvas-body mobile-menu__body">
                        <nav class="mobile-menu__nav font-title">
                            <a :href="route('price.index')" class="mobile-menu__link">Pricing</a>
                            <a :href="route('help.index')" class="mobile-menu__link">Help</a>
                        </nav>
                        <div class="mobile-menu__actions" v-if="isLoggedIn">
                            <Link
                                :href="route('dashboard')"
                                class="amo-btn amo-btn--primary mobile-menu__btn"
                            >
                                Dashboard
                            </Link>
                        </div>
                        <div class="mobile-menu__actions" v-else>
                            <Link
                                :href="signupHref"
                                class="amo-btn amo-btn--primary mobile-menu__btn"
                            >
                                {{ props.signupButtonText }}
                            </Link>
                            <a
                                :href="route('login')"
                                class="amo-btn amo-btn--secondary mobile-menu__btn"
                            >
                                Login
                            </a>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    </header>
    <!-- Page wrapper-->
    <main
        class="w-full mx-auto main-content"
        :class="props.fullWidth ? 'max-w-7xl' : 'max-w-6xl'"
    >
        <slot />
    </main>

    <CookieConsentBanner />

    <!-- Footer Section -->
    <footer class="footer w-full max-w-6xl mx-auto">
        <div class="footer-content">
            <div class="footer-header">
                <Logo color="white" size="lg" />
                <ul class="nav-links" v-if="false">
                    <li><a href="#">Home</a></li>
                    <li><a href="#">Industries</a></li>
                    <li><a href="#">Blog</a></li>
                    <li><a href="#">Contact</a></li>
                </ul>
            </div>
            <div class="footer-container font-title">
                <div class="footer-section">
                    <h2>Features</h2>
                    <ul>
                        <li>
                            <a :href="signupHref">
                                <span class="footer-link-icon"></span>
                                Get your Allmy.online profile
                            </a>
                        </li>
                        <li>
                            <a :href="route('manager.register')">
                                <span class="footer-link-icon"></span>
                                Sign up as an account manager
                            </a>
                        </li>
                    </ul>
                </div>

                <div class="footer-section">
                    <h2>Company</h2>
                    <ul>
                        <li>
                            <a :href="route('contact')"
                                ><span class="footer-link-icon"></span>
                                Contact</a
                            >
                        </li>
                        <li>
                            <a :href="route('about')"
                                ><span class="footer-link-icon"></span> About</a
                            >
                        </li>
                        <li>
                            <a :href="route('investors')"
                                ><span class="footer-link-icon"></span>
                                Investors</a
                            >
                        </li>
                    </ul>
                </div>

                <div class="footer-section d-none d-lg-block d-md-block">
                    <h2>Support</h2>
                    <ul>
                        <li>
                            <a :href="route('help.index')"
                                ><span class="footer-link-icon"></span> Help
                                Center</a
                            >
                        </li>
                        <li>
                            <a href="#"
                                ><span class="footer-link-icon"></span> FAQs</a
                            >
                        </li>
                    </ul>
                </div>
                <div class="footer-section d-none d-lg-block d-md-block">
                    <h2>Trust & Legal</h2>
                    <ul>
                        <li>
                            <a :href="route('terms-of-service')"
                                ><span class="footer-link-icon"></span> Terms &
                                Conditions</a
                            >
                        </li>
                        <li>
                            <a :href="route('privacy')"
                                ><span class="footer-link-icon"></span> Privacy
                                Policy</a
                            >
                        </li>
                    </ul>
                </div>
            </div>
            <div class="footer-bottom">
                <div class="text-white text-sm opacity-75">
                    &copy; {{ new Date().getFullYear() }} All My Online LLC. AllMyOnline is operated by All My Online LLC.
                </div>
                <div class="social-icons">
                    <a href="#"
                        ><img
                            src="/template/allmyonline/assets/Instagram.png"
                            alt="Instagram"
                    /></a>
                    <a href="#"
                        ><img
                            src="/template/allmyonline/assets/Facebook.png"
                            alt="Facebook"
                    /></a>
                    <a href="#"
                        ><img
                            src="/template/allmyonline/assets/Twitter.png"
                            alt="Twitter"
                    /></a>
                </div>
            </div>
        </div>
    </footer>
</template>
<style scoped>
.mobile-menu {
    width: 100%;
    border-left: none;
    background-color: var(--background-primary);
}
.mobile-menu__header {
    padding: 1.25rem 1.5rem;
}
.mobile-menu__body {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
}
.mobile-menu__nav {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    margin-top: 1rem;
}
.mobile-menu__link {
    display: flex;
    align-items: center;
    padding: 1rem 1.25rem;
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-primary);
    text-decoration: none;
    border-radius: 1rem;
    transition: background-color 0.15s ease;
}
.mobile-menu__link:hover,
.mobile-menu__link:active {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
}
.mobile-menu__actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: auto;
    padding-top: 1.5rem;
    padding-bottom: 1rem;
    border-top: 1px solid var(--border-color);
}
.mobile-menu__btn {
    width: 100%;
    justify-content: center;
    padding: 1rem;
    font-size: 1.125rem;
}
.header {
    padding: 1rem 0 2rem;
}
.nav-links {
    padding: 0;
}
.nav-links a,
.amo-logo a {
    color: var(--text-primary);
    text-decoration: none;
}
</style>
<style>
.main-content > section + section {
    margin-top: 3rem;
}
</style>
<script>
import Logo from "@/Pages/Appearance/Partials/Logo.vue";
export default {};
</script>
